import { memo, useCallback, useMemo, useRef, useState, type RefObject } from 'react';
import clsx from 'clsx';
import { Clock, Gauge, Timer } from 'lucide-react';
import type { PipeOption } from '../../../ndt/components/FilterPanel';
import { formatAnalyticsDateTime, formatDuration } from '../../../../lib/formatUtils';
import type { TimelinePoint } from '../../utils/weldDashboardMetrics';
import { cardClass, DASHBOARD_COLORS } from './dashboardTokens';
import { DashboardSectionHeader } from './DashboardSectionHeader';
import { ChartZoomControls } from './ChartZoomControls';
import { FloatingChartTooltip } from './FloatingChartTooltip';
import { useChartWidthZoom } from './useChartWidthZoom';

interface PassFailTimelineProps {
  data: TimelinePoint[];
  pipeOptions?: PipeOption[];
  onStickClick?: (point: TimelinePoint) => void;
}

interface PipeTimelineRow {
  pipeId: string;
  label: string;
  points: TimelinePoint[];
}

const OUTCOME_COLOR: Record<TimelinePoint['outcome'], string> = {
  pass: DASHBOARD_COLORS.pass,
  fail: DASHBOARD_COLORS.fail,
  other: DASHBOARD_COLORS.other,
};

const OUTCOME_LABEL: Record<TimelinePoint['outcome'], string> = {
  pass: 'Within limit',
  fail: 'Out of limit',
  other: 'Other',
};

const OUTCOME_ACCENT: Record<TimelinePoint['outcome'], string> = {
  pass: 'border-l-galvanizing-green',
  fail: 'border-l-hero-red',
  other: 'border-l-heritage-grey',
};

const OUTCOME_PILL: Record<TimelinePoint['outcome'], string> = {
  pass: 'bg-[rgba(31,154,58,0.08)] text-galvanizing-green border-galvanizing-green/30',
  fail: 'bg-[rgba(196,0,31,0.06)] text-readable-red border-hero-red-20',
  other: 'bg-[rgba(0,39,63,0.04)] text-heritage-grey border-[#E2E5EA]',
};

const STICK_HEIGHT = 22;
const STICK_GAP = 2;
const STICK_MIN_WIDTH = 4;
const STICK_MAX_WIDTH = 24;
const ROW_HEIGHT = 32;
const PIPE_LABEL_WIDTH = 176;
const STRIP_MIN_WIDTH = 280;
const UNKNOWN_PIPE_ID = '__unknown__';

function formatTimelineDuration(sec: number | null): string {
  if (sec == null) return '—';
  const formatted = formatDuration(sec / 60);
  return typeof formatted === 'string' ? formatted : String(formatted);
}

function pipeLabelForId(pipeId: string, labelByPipeId: Map<string, string>): string {
  if (pipeId === UNKNOWN_PIPE_ID) return 'Unknown pipe';
  return labelByPipeId.get(pipeId) ?? `Pipe ${pipeId}`;
}

function groupTimelineByPipe(
  data: TimelinePoint[],
  pipeOptions: PipeOption[],
): PipeTimelineRow[] {
  const labelByPipeId = new Map(
    pipeOptions.map((p) => [String(p.pipeId), p.displayName || String(p.pipeId)]),
  );

  const buckets = new Map<string, TimelinePoint[]>();
  data.forEach((point) => {
    const key = point.pipeId?.trim() ? point.pipeId : UNKNOWN_PIPE_ID;
    const list = buckets.get(key) ?? [];
    list.push(point);
    buckets.set(key, list);
  });

  if (buckets.size === 0) return [];

  const orderedPipeIds: string[] = [];
  pipeOptions.forEach((p) => {
    const id = String(p.pipeId);
    if (buckets.has(id)) orderedPipeIds.push(id);
  });
  buckets.forEach((_points, pipeId) => {
    if (!orderedPipeIds.includes(pipeId)) orderedPipeIds.push(pipeId);
  });

  return orderedPipeIds
    .map((pipeId) => ({
      pipeId,
      label: pipeLabelForId(pipeId, labelByPipeId),
      points: buckets.get(pipeId) ?? [],
    }))
    .filter((row) => row.points.length > 0);
}

function computeStickWidth(count: number, stripWidth: number): number {
  if (count <= 0) return STICK_MIN_WIDTH;
  const available = Math.max(stripWidth - Math.max(0, count - 1) * STICK_GAP, count * STICK_MIN_WIDTH);
  const raw = available / count;
  return Math.min(STICK_MAX_WIDTH, Math.max(STICK_MIN_WIDTH, raw));
}

function TimelineTooltipContent({ point }: { point: TimelinePoint }) {
  const when = point.timestampMs != null ? formatAnalyticsDateTime(point.timestampMs) : null;

  const stats = [
    {
      key: 'duration',
      icon: Timer,
      label: 'Duration',
      value: formatTimelineDuration(point.durationSec),
    },
    ...(point.qualityScore != null
      ? [
          {
            key: 'quality',
            icon: Gauge,
            label: 'Quality',
            value: point.qualityScore.toFixed(1),
          },
        ]
      : []),
    ...(when
      ? [
          {
            key: 'date',
            icon: Clock,
            label: 'Date',
            value: when.date,
          },
          {
            key: 'time',
            icon: Clock,
            label: 'Time',
            value: when.time,
          },
        ]
      : []),
  ];

  return (
    <div
      className={clsx(
        'rounded-xl border border-[#E2E5EA] bg-white px-4 py-3 shadow-lg',
        'border-l-[3px]',
        OUTCOME_ACCENT[point.outcome],
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-heading text-sm font-semibold text-blueprint-blue">
          Weld #{point.weldNumber}
        </span>
        <span
          className={clsx(
            'rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
            OUTCOME_PILL[point.outcome],
          )}
        >
          {OUTCOME_LABEL[point.outcome]}
        </span>
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.key}
              className="flex min-w-[120px] items-center gap-2 rounded-xl bg-[rgba(0,39,63,0.04)] px-3 py-2"
            >
              <Icon className="h-4 w-4 shrink-0 text-heritage-grey" />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-heritage-grey">
                  {stat.label}
                </p>
                <p className="truncate text-sm font-medium text-blueprint-blue">{stat.value}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const PassFailTimeline = memo(({ data, pipeOptions = [], onStickClick }: PassFailTimelineProps) => {
  const pipeRows = useMemo(() => groupTimelineByPipe(data, pipeOptions), [data, pipeOptions]);
  const maxPointsInRow = useMemo(
    () => pipeRows.reduce((max, row) => Math.max(max, row.points.length), 0),
    [pipeRows],
  );
  const isEmpty = pipeRows.length === 0;
  const { scrollRef, targetZoom, displayZoom, chartPixelWidth, zoomIn, zoomOut, resetZoom } =
    useChartWidthZoom({ enabled: !isEmpty });

  return (
    <div className={cardClass}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <DashboardSectionHeader
          title="WPS limit timeline"
          subtitle="Each segment is one weld — grouped by pipe size; click a segment to drill down"
          helpKey="wps_limit_timeline"
          className="mb-0 min-w-0 flex-1"
        />
        {!isEmpty && (
          <ChartZoomControls
            targetZoom={targetZoom}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
            onReset={resetZoom}
          />
        )}
      </div>

      {isEmpty ? (
        <div className="flex h-24 items-center justify-center text-sm text-heritage-grey">
          No welds to display
        </div>
      ) : (
        <TimelineContent
          pipeRows={pipeRows}
          scrollRef={scrollRef}
          chartPixelWidth={chartPixelWidth}
          displayZoom={displayZoom}
          maxPointsInRow={maxPointsInRow}
          onStickClick={onStickClick}
        />
      )}
    </div>
  );
});

interface TimelineContentProps {
  pipeRows: PipeTimelineRow[];
  scrollRef: RefObject<HTMLDivElement>;
  chartPixelWidth: number | undefined;
  displayZoom: number;
  maxPointsInRow: number;
  onStickClick?: (point: TimelinePoint) => void;
}

function TimelineContent({
  pipeRows,
  scrollRef,
  chartPixelWidth,
  displayZoom,
  maxPointsInRow,
  onStickClick,
}: TimelineContentProps) {
  const [hoveredPoint, setHoveredPoint] = useState<TimelinePoint | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const stickRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const baseStripWidth = Math.max(maxPointsInRow * (STICK_MIN_WIDTH + STICK_GAP), STRIP_MIN_WIDTH);
  const stripWidth =
    chartPixelWidth != null
      ? Math.max(chartPixelWidth - PIPE_LABEL_WIDTH, baseStripWidth * displayZoom)
      : baseStripWidth * displayZoom;

  const stickWidth = computeStickWidth(maxPointsInRow, stripWidth);
  const contentWidth = PIPE_LABEL_WIDTH + stripWidth;

  const stickKey = (pipeId: string, point: TimelinePoint, index: number) =>
    `${pipeId}-${point.weldNumber}-${point.timestampMs ?? index}`;

  const handleStickEnter = useCallback((point: TimelinePoint, key: string) => {
    setHoveredPoint(point);
    const el = stickRefs.current.get(key);
    setAnchorRect(el ? el.getBoundingClientRect() : null);
  }, []);

  const handleStickLeave = useCallback(() => {
    setHoveredPoint(null);
    setAnchorRect(null);
  }, []);

  return (
    <>
      <div
        ref={scrollRef}
        className="mt-4 overflow-x-scroll overflow-y-visible overscroll-contain pb-1"
        style={{ scrollbarGutter: 'stable' }}
      >
        <div style={{ minWidth: contentWidth }} className="flex flex-col gap-2">
          {pipeRows.map((row) => (
            <div key={row.pipeId} className="flex items-center gap-3" style={{ minHeight: ROW_HEIGHT }}>
              <div
                className="shrink-0 truncate text-xs font-medium leading-tight text-blueprint-blue"
                style={{ width: PIPE_LABEL_WIDTH }}
                title={row.label}
              >
                {row.label}
              </div>
              <div
                className="flex items-center"
                style={{
                  width: stripWidth,
                  gap: STICK_GAP,
                  minHeight: ROW_HEIGHT,
                }}
                role="img"
                aria-label={`WPS limit timeline for ${row.label}`}
              >
                {row.points.map((point, index) => {
                  const key = stickKey(row.pipeId, point, index);
                  const isHovered =
                    hoveredPoint?.weldNumber === point.weldNumber &&
                    hoveredPoint?.timestampMs === point.timestampMs &&
                    hoveredPoint?.pipeId === point.pipeId;
                  return (
                    <button
                      key={key}
                      ref={(el) => {
                        if (el) stickRefs.current.set(key, el);
                        else stickRefs.current.delete(key);
                      }}
                      type="button"
                      className={clsx(
                        'shrink-0 rounded-[4px] transition-transform duration-150',
                        'focus:outline-none focus:ring-2 focus:ring-blueprint-blue/30',
                        isHovered && 'scale-110 ring-2 ring-blueprint-blue/25',
                        onStickClick && 'cursor-pointer',
                      )}
                      style={{
                        width: stickWidth,
                        height: STICK_HEIGHT,
                        backgroundColor: OUTCOME_COLOR[point.outcome],
                      }}
                      onMouseEnter={() => handleStickEnter(point, key)}
                      onMouseLeave={handleStickLeave}
                      onFocus={() => handleStickEnter(point, key)}
                      onBlur={handleStickLeave}
                      onClick={() => onStickClick?.(point)}
                      aria-label={`Weld ${point.weldNumber}: ${OUTCOME_LABEL[point.outcome]}`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <FloatingChartTooltip open={hoveredPoint != null} anchorRect={anchorRect}>
        {hoveredPoint && <TimelineTooltipContent point={hoveredPoint} />}
      </FloatingChartTooltip>

      <div className="mt-3 flex flex-wrap gap-4 text-xs text-heritage-grey">
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-2.5 rounded-[3px] bg-galvanizing-green" />
          Within limit
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-2.5 rounded-[3px] bg-hero-red" />
          Out of limit
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-2.5 rounded-[3px] bg-heritage-grey" />
          Other
        </span>
      </div>
    </>
  );
}

PassFailTimeline.displayName = 'PassFailTimeline';
